import React from "react";

interface SkeletonProps {
    className?: string;
    variant?: "rect" | "circle" | "text";
    width?: string | number;
    height?: string | number;
}

const Skeleton: React.FC<SkeletonProps> = ({ className = "", variant = "rect", width, height }) => {
    const baseClass = "animate-pulse bg-gray-200";
    const variantClass = variant === "circle" ? "rounded-full" : "rounded-md";

    return (
        <div
            className={`${baseClass} ${variantClass} ${className}`}
            style={{ width, height }}
        />
    );
};

export default Skeleton;

export const CarCardSkeleton = () => (
    <div className="border border-gray-100 rounded-xl p-3 w-full bg-white shadow-sm flex flex-col gap-3">
        {/* Matches ProductCard's aspect-[11/6] image box so swapping in the
            real card doesn't reflow the page once data arrives. */}
        <div className="w-full aspect-[11/6]">
            <Skeleton height="100%" width="100%" className="rounded-lg" />
        </div>
        <div className="space-y-2 px-1">
            <Skeleton height={18} width="80%" />
            <Skeleton height={16} width="50%" />
        </div>
        <div className="mt-auto px-1 flex flex-col gap-2">
            <Skeleton height={36} className="w-full rounded-lg" />
            <Skeleton height={14} width="40%" className="mx-auto" />
        </div>
    </div>
);

export const StoryCardSkeleton = () => (
    <div className="relative aspect-[9/16] w-full rounded-xl overflow-hidden bg-gray-100">
        <Skeleton height="100%" width="100%" />
        <div className="absolute bottom-4 left-3 right-3 space-y-2">
            <Skeleton height={14} width="80%" className="bg-gray-300/50" />
            <Skeleton height={14} width="60%" className="bg-gray-300/50" />
        </div>
    </div>
);

export const ProductGridSkeleton = ({ count = 4 }: { count?: number }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: count }).map((_, i) => (
            <CarCardSkeleton key={i} />
        ))}
    </div>
);

export const NewsSkeleton = () => (
    <div className="flex flex-col gap-3">
        {/* Matches NewsCard's aspect-[2/1] image box so swapping in the real
            card doesn't reflow the page once data arrives. */}
        <div className="w-full aspect-[2/1]">
            <Skeleton height="100%" width="100%" className="rounded-xl" />
        </div>
        <Skeleton height={20} width="90%" />
        <Skeleton height={16} width="60%" />
    </div>
);

export const BrandSkeleton = () => (
    <div className="flex flex-col items-center justify-center p-4 border border-gray-50 rounded-lg">
        {/* Matches BrandsGrid's fixed 90x60 rectangular logo box (BrandsGrid.tsx
            BRAND_LOGO_WIDTH/HEIGHT) — the real logos are never circular. */}
        <Skeleton width={90} height={60} className="mb-2" />
        <Skeleton height={14} width={50} />
    </div>
);

export const SectionSkeleton = ({ titleHeight = 30, contentCount = 4, type = "product" }: { titleHeight?: number, contentCount?: number, type?: "product" | "news" | "brand" | "story" }) => (
    <div className="my-6">
        <Skeleton height={titleHeight} width={200} className="mb-6" />
        {type === "product" && <ProductGridSkeleton count={contentCount} />}
        {type === "news" && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {Array.from({ length: contentCount }).map((_, i) => (
                    <NewsSkeleton key={i} />
                ))}
            </div>
        )}
        {type === "brand" && (
            // Column counts matched to BrandsGrid's real desktop grid
            // (grid-cols-3 md:grid-cols-4 lg:grid-cols-6) to minimize reflow.
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {Array.from({ length: contentCount }).map((_, i) => (
                    <BrandSkeleton key={i} />
                ))}
            </div>
        )}
        {type === "story" && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {Array.from({ length: contentCount }).map((_, i) => (
                    <StoryCardSkeleton key={i} />
                ))}
            </div>
        )}
    </div>
);
